import React from 'react';
import Grid from '@material-ui/core/Grid';
import { Button, Theme, WithStyles } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import createStyles from '@material-ui/core/styles/createStyles';
import withStyles from '@material-ui/core/styles/withStyles';
import InputRules from './InputRules';
import DistributionSummaryPlot from '../Distributions/DistributionSummaryPlot';
import DistributionPlaygroundDialog from '../Dialogs/DistributionPlaygroundDialog';
import UniformInput from './UniformInput';
import NormalInput from './NormalInput';
import WeibullInput from './WeibullInput';
import ProxyTypes, {
  DistributionDictionary,
  DistributionTimeDependence, ProxyTypeDisplayNames,
} from '../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/ProxyTypes';
import ExponentialInput from './ExponentialInput';
import LogNormalInput from './LogNormalInput';
import Expression, { ExpressionJSON } from '../../../../../mxGraph/HCLTreeNode/HCLTreeVertexValue/Expression';
import NonParametricInput from './NonParametricInput';

const styles = (theme: Theme) => createStyles({
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

interface Props extends WithStyles<typeof styles> {
  expression: Expression;
  currentProxyType: ProxyTypes;
  onTimeDependenceChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  onDistributionTypeChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  onTextFieldChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>, error?: boolean, multiple?: boolean) => void;
  onTraceSelectFromPlayground: (e: React.MouseEvent<HTMLElement, MouseEvent>, selectedTrace: ExpressionJSON) => void;
  disabled?: boolean;
  override?: {
    type: ProxyTypes,
    eventType?: DistributionTimeDependence,
    distributionType?: ProxyTypes.NORMAL_DISTRIBUTION | ProxyTypes.LOG_NORMAL_DISTRIBUTION | ProxyTypes.UNIFORM_DISTRIBUTION | ProxyTypes.WEIBULL_DISTRIBUTION | ProxyTypes.EXPONENTIAL_DISTRIBUTION | ProxyTypes.NON_PARAMETRIC_DISTRIBUTION
    params?: ["mean", "std"] | ["error_factor", "median"] | ["p5", "p95"]
  }
}

interface State {
  isPlaygroundOpen: boolean;
}

class DistributionInput extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isPlaygroundOpen: false,
    };
  }

  private handlePlaygroundOpen = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    this.setState({ isPlaygroundOpen: true });
  };

  private handlePlaygroundClose = (e: React.SyntheticEvent<{}>) => {
    this.setState({ isPlaygroundOpen: false });
  };

  render() {
    const {
      classes, expression, currentProxyType, onTimeDependenceChange, onDistributionTypeChange, onTextFieldChange, onTraceSelectFromPlayground, disabled, override
    } = this.props;
    const { isPlaygroundOpen } = this.state;

    return (
      <Grid container item spacing={10}>
        <Grid item xs={12} sm={4} md={3} lg={3} xl={3}>
          <TextField
            required
            label="Type of Event"
            name="distribution_time_dependence"
            value={override && override.eventType ? override.eventType : expression.getDistributionTimeDependence()}
            error={!expression.getDistributionTimeDependence()}
            helperText={expression.getDistributionTimeDependence() ? undefined : InputRules.DESCRIPTIONS.EMPTY}
            onChange={onTimeDependenceChange}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            fullWidth
            select
            disabled={disabled || override && 'eventType' in override}
            SelectProps={{
              MenuProps: {
                disablePortal: true,
              },
            }}
            variant="outlined"
          >
            {
              Object.values(DistributionTimeDependence).map(distributionTimeDependence => (
                <MenuItem key={distributionTimeDependence} value={distributionTimeDependence}>
                  {distributionTimeDependence}
                </MenuItem>
              ))
            }
          </TextField>
          {
            expression.getDistributionTimeDependence()
            && (
              <React.Fragment>
                <TextField
                  required
                  label="Distribution Type"
                  name="_proxy"
                  value={override && override.distributionType ? override.distributionType : currentProxyType}
                  error={currentProxyType === ProxyTypes.DISTRIBUTION}
                  helperText={currentProxyType !== ProxyTypes.DISTRIBUTION ? undefined : InputRules.DESCRIPTIONS.EMPTY}
                  onChange={onDistributionTypeChange}
                  margin="normal"
                  fullWidth
                  select
                  disabled={disabled || override && 'distributionType' in override}
                  SelectProps={{
                    MenuProps: {
                      disablePortal: true,
                    },
                  }}
                  variant="outlined"
                >
                  {
                    DistributionDictionary[expression.getDistributionTimeDependence()].map(proxyType => (
                      <MenuItem key={proxyType} value={proxyType}>
                        {ProxyTypeDisplayNames[proxyType]}
                      </MenuItem>
                    ))
                  }
                </TextField>
                {
                  DistributionDictionary[expression.getDistributionTimeDependence()].includes(currentProxyType)
                  && (
                    <React.Fragment>
                      {
                        currentProxyType === ProxyTypes.EXPONENTIAL_DISTRIBUTION
                        && (
                          <ExponentialInput
                            testInterval={expression.getExponentialTestInterval()}
                            failureRate={expression.getFailureRate()}
                            onChange={onTextFieldChange}
                          />
                        )
                      }
                      {
                        currentProxyType === ProxyTypes.LOG_NORMAL_DISTRIBUTION
                        && (
                          <LogNormalInput
                            logNormalParams={expression.getLogNormalParams()}
                            logNormalMean={expression.getLogNormalMean()}
                            logNormalStd={expression.getLogNormalStd()}
                            logNormalMedian={expression.getLogNormalMedian()}
                            logNormalErrorFactor={expression.getLogNormalErrorFactor()}
                            logNormalP5={expression.getLogNormalP5()}
                            logNormalP95={expression.getLogNormalP95()}
                            onChange={onTextFieldChange}
                            disabled={disabled}
                          />
                        )
                      }
                      {
                        currentProxyType === ProxyTypes.WEIBULL_DISTRIBUTION
                        && (
                          <WeibullInput
                            testInterval={expression.getWeibullTestInterval()}
                            shape={expression.getShape()}
                            scale={expression.getScale()}
                            onChange={onTextFieldChange}
                          />
                        )
                      }
                      {
                        currentProxyType === ProxyTypes.NORMAL_DISTRIBUTION
                        && (
                          <NormalInput
                            normalParams={expression.getNormalParams()}
                            normalMean={expression.getNormalMean()}
                            normalStd={expression.getNormalStd()}
                            normalMedian={expression.getNormalMedian()}
                            normalErrorFactor={expression.getNormalErrorFactor()}
                            normalP5={expression.getNormalP5()}
                            normalP95={expression.getNormalP95()}
                            onChange={onTextFieldChange}
                          />
                        )
                      }
                      {
                        currentProxyType === ProxyTypes.UNIFORM_DISTRIBUTION
                        && (
                          <UniformInput
                            max={expression.getUniformMax()}
                            min={expression.getUniformMin()}
                            onChange={onTextFieldChange}
                          />
                        )
                      }
                      {
                        currentProxyType === ProxyTypes.NON_PARAMETRIC_DISTRIBUTION
                        && (
                          <NonParametricInput
                            timeToFailure={expression.getTimeToFailure()}
                            estimatedReliability={expression.getEstimatedReliability()}
                            onChange={onTextFieldChange}
                          />
                        )
                      }
                    </React.Fragment>
                  )
                }
              </React.Fragment>
            )
          }
          <div className={classes.buttonContainer}>
            <Button onClick={this.handlePlaygroundOpen}>Open Distribution Playground</Button>
            <DistributionPlaygroundDialog
              open={isPlaygroundOpen}
              onClose={this.handlePlaygroundClose}
              onSelect={onTraceSelectFromPlayground}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={8} md={9} lg={9} xl={9}>
          <DistributionSummaryPlot
            distributionSummaries={[expression.toDistributionSummary()]}
            timeDependence={expression.getDistributionTimeDependence()}
          />
        </Grid>
      </Grid>
    );
  }
}

export default withStyles(styles)(DistributionInput);
